import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, Outlet, NavLink, useLocation } from "react-router-dom";
import { LogOut, LayoutGrid, LifeBuoy, FolderArchive, Calendar, MessageSquare, ArrowLeft, Plus, ListChecks, Video, User, Menu, Home } from "lucide-react";
import { Brand } from "../components/Logo";
import { useAuth } from "../contexts/AuthContext";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { StatusChip, STATUS_LABELS, fmtDateTime } from "./tickets/_shared";
import ClientLogo from "./clients/ClientLogo";
import { CellulariList, SitoWebLink } from "./clients/DatiSocietariShared";
import ClientDashboard from "./dashboard/ClientDashboard";
import MaraFooter from "../components/MaraFooter";

const NAV = [
    { key: "today",    label: "Oggi",       Icon: Home,          to: "/portal", enabled: true, end: true },
    { key: "overview", label: "Panoramica", Icon: LayoutGrid,    to: "/portal/panoramica", enabled: true, end: true },
    { key: "comms",    label: "Comunicazioni", Icon: MessageSquare, to: "/portal/comunicazioni", enabled: true },
    { key: "tickets",  label: "Assistenza", Icon: LifeBuoy,   to: "/portal/ticket", enabled: true },
    { key: "deadlines",label: "Scadenze",   Icon: Calendar,   to: "/portal/scadenze", enabled: true },
    { key: "meetings", label: "Riunioni",   Icon: Video,      to: "/portal/riunioni", enabled: true },
    { key: "docs",     label: "Documenti",  Icon: FolderArchive, to: "/portal/documenti", enabled: true },
    { key: "checklist",label: "Checklist",  Icon: ListChecks,    to: "/portal/checklist", enabled: true },
    { key: "profile",  label: "Il mio profilo", Icon: User,   to: "/portal/profilo", enabled: true },
];

function PortalSidebar({ drawerOpen, onNavClick }) {
    return (
        <aside
            className={"lt-sidebar flex flex-col" + (drawerOpen ? " is-open" : "")}
            style={{ padding: "24px 16px" }}
            data-testid="portal-sidebar"
            aria-hidden={drawerOpen ? "false" : undefined}
        >
            <div style={{ padding: "0 8px" }}><Brand logoSize={24} tone="light" /></div>
            <div style={{ height: 1, background: "#E3E5E1", margin: "20px -16px 16px" }} />
            <nav className="flex flex-col gap-1">
                {NAV.map((it) => {
                    const Icon = it.Icon;
                    if (!it.enabled) return (
                        <div key={it.key} className="lt-nav-item lt-nav-item--disabled" data-testid={`portal-nav-${it.key}`}>
                            <Icon size={16} strokeWidth={1.75} /><span>{it.label}</span>
                        </div>
                    );
                    return (
                        <NavLink key={it.key} to={it.to} end={it.end} onClick={onNavClick} className={({ isActive }) => "lt-nav-item " + (isActive ? "is-active" : "")} data-testid={`portal-nav-${it.key}`}>
                            <Icon size={16} strokeWidth={1.75} /><span>{it.label}</span>
                        </NavLink>
                    );
                })}
            </nav>
        </aside>
    );
}

export default function PortalShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const handleLogout = async () => { await logout(); navigate("/login"); };
    const closeDrawer = () => setDrawerOpen(false);
    // FIX RIUNIONI 2026-11-02: fullscreen reale su /portal/riunioni/:id/join
    const isInCall = /\/riunioni\/[^/]+\/join\b/.test(location.pathname);
    if (isInCall) {
        return (
            <div data-testid="client-portal-shell-incall"
                 style={{ height: "100vh", width: "100vw", overflow: "hidden" }}>
                <Outlet />
            </div>
        );
    }
    return (
        <div className="lt-root-shell" data-testid="client-portal-shell">
            <PortalSidebar drawerOpen={drawerOpen} onNavClick={closeDrawer} />
            <div
                className={"lt-mobile-overlay" + (drawerOpen ? " is-open" : "")}
                onClick={closeDrawer}
                aria-hidden="true"
                data-testid="portal-mobile-overlay"
            />
            <div className="flex flex-col min-w-0">
                <header className="lt-topbar" data-testid="portal-topbar">
                    <button
                        type="button"
                        className="lt-mobile-hamburger"
                        onClick={() => setDrawerOpen((v) => !v)}
                        aria-label="Apri menu"
                        aria-expanded={drawerOpen}
                        data-testid="portal-hamburger-btn"
                    >
                        <Menu size={20} strokeWidth={2} />
                    </button>
                    <span className="lt-username" data-testid="portal-user-name">{user?.name}</span>
                    <div style={{ display: "flex", alignItems: "center", padding: "0 16px" }}><span className="lt-divider-v" /></div>
                    <button className="lt-btn-ghost" onClick={handleLogout} data-testid="portal-logout-btn"><LogOut size={16} /><span>Esci</span></button>
                </header>
                <main className="flex-1" style={{ background: "#F4F5F2", overflowY: "auto", display: "flex", flexDirection: "column" }} data-testid="portal-main">
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <Outlet />
                    </div>
                    <MaraFooter variant="dark" />
                </main>
            </div>
        </div>
    );
}

// === Portal Home (FASE C: Dashboard Oggi) ===
export function PortalHome() {
    return <ClientDashboard />;
}

// === Portal Panoramica (dati societari — spostata da / a /panoramica) ===
function StatusChipReadonly({ status }) {
    return status === "active" ? <span className="lt-chip lt-chip--active"><span className="lt-chip-dot" />Attiva</span>
        : <span className="lt-chip lt-chip--inactive">Inattiva</span>;
}
export function PortalPanoramica() {
    const [company, setCompany] = useState(null);
    const [notes, setNotes] = useState([]);
    const [contacts, setContacts] = useState([]);
    useEffect(() => { api.get("/client/me/company").then(({ data }) => setCompany(data)).catch(() => {}); }, []);
    useEffect(() => { api.get("/portal/clients/me/notes", { params: { since_days: 7 } }).then(({ data }) => setNotes(data.items || [])).catch(() => {}); }, []);
    useEffect(() => { api.get("/portal/clients/me/contacts").then(({ data }) => setContacts(data.items || [])).catch(() => {}); }, []);
    const [allNotes, setAllNotes] = useState([]);
    useEffect(() => { api.get("/portal/clients/me/notes").then(({ data }) => setAllNotes(data.items || [])).catch(() => {}); }, []);
    if (!company) return <div style={{ padding: "32px 40px", color: "#8A8F88" }}>Caricamento…</div>;
    return (
        <div style={{ padding: "32px 40px", maxWidth: 1000 }}>
            <div className="flex items-center gap-5" style={{ marginBottom: 28, flexWrap: "wrap" }}>
                <ClientLogo
                    client={{ company_name: company.company_name, has_logo: !!company.has_logo }}
                    endpoint={`${API}/client/logo`}
                    size={96}
                />
                <div>
                    <h1 className="lt-h1" data-testid="portal-heading">{company.company_name}</h1>
                    <p className="lt-sub" style={{ marginTop: 6 }}>Panoramica del tuo account ENERLEFT.</p>
                </div>
            </div>

            {notes.length > 0 && (
                <div className="lt-panel" style={{ padding: 24, marginBottom: 20, borderLeft: "3px solid #E5FF00" }} data-testid="portal-widget-novita">
                    <h2 className="lt-section-title" style={{ marginBottom: 12 }}>Novità per te</h2>
                    <div style={{ display: "grid", gap: 12 }}>
                        {notes.slice(0, 5).map((n) => (
                            <div key={n.id} style={{ padding: 12, background: "#FAFAF7", borderRadius: 3 }} data-testid={`portal-novita-${n.id}`}>
                                <div style={{ fontSize: 12, color: "#8A8F88", fontFamily: '"IBM Plex Mono", monospace', letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>
                                    {n.author} · {new Date(n.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                                </div>
                                <div style={{ fontSize: 14, color: "#181B20", whiteSpace: "pre-wrap" }}>{n.body}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="lt-panel" style={{ padding: 24 }}>
                <h2 className="lt-section-title">Dati società</h2>
                <dl style={{ margin: 0 }}>
                    {[["Ragione sociale", company.company_name], ["P.IVA o CF", company.vat_or_taxcode], ["Indirizzo", company.address || "—"], ["PEC", company.pec_email]].map(([k, v]) => (
                        <div key={k} style={{ display: "grid", gridTemplateColumns: "180px 1fr", padding: "10px 0", borderBottom: "1px solid #EEEFEA" }}>
                            <dt className="lt-label" style={{ margin: 0 }}>{k}</dt>
                            <dd style={{ margin: 0, color: "#181B20", fontSize: 14 }}>{v}</dd>
                        </div>
                    ))}
                    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", padding: "10px 0" }}>
                        <dt className="lt-label" style={{ margin: 0 }}>Stato</dt>
                        <dd style={{ margin: 0 }}><StatusChipReadonly status={company.status} /></dd>
                    </div>
                </dl>
            </div>

            <div className="lt-panel" style={{ padding: 24, marginTop: 20 }} data-testid="portal-dati-societari">
                <h2 className="lt-section-title">Dati societari</h2>
                <dl style={{ margin: 0 }}>
                    {[
                        ["Capitale sociale", company.capitale_sociale || "—", "capitale-sociale"],
                        ["Numero REA", company.numero_rea || "—", "numero-rea"],
                        ["Amministratore", company.amministratore || "—", "amministratore"],
                        ["Sede legale", company.sede_legale || "—", "sede-legale"],
                        ["Sede operativa", company.sede_operativa || "—", "sede-operativa"],
                    ].map(([k, v, tid]) => (
                        <div key={k} style={{ display: "grid", gridTemplateColumns: "180px 1fr", padding: "10px 0", borderBottom: "1px solid #EEEFEA" }}>
                            <dt className="lt-label" style={{ margin: 0 }}>{k}</dt>
                            <dd style={{ margin: 0, color: "#181B20", fontSize: 14 }} data-testid={`portal-${tid}`}>{v}</dd>
                        </div>
                    ))}
                    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", padding: "10px 0", borderBottom: "1px solid #EEEFEA" }}>
                        <dt className="lt-label" style={{ margin: 0 }}>Cellulari</dt>
                        <dd style={{ margin: 0, color: "#181B20", fontSize: 14 }} data-testid="portal-cellulari">
                            <CellulariList items={company.cellulari} testid="portal-cellulari-list" />
                        </dd>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", padding: "10px 0" }}>
                        <dt className="lt-label" style={{ margin: 0 }}>Sito web</dt>
                        <dd style={{ margin: 0, color: "#181B20", fontSize: 14 }} data-testid="portal-sito-web">
                            <SitoWebLink value={company.sito_web} testid="portal-sito-web-link" />
                        </dd>
                    </div>
                </dl>
            </div>

            {contacts.length > 0 && (
                <div className="lt-panel" style={{ padding: 24, marginTop: 20 }} data-testid="portal-contatti-enerleft">
                    <h2 className="lt-section-title">Contatti ENERLEFT</h2>
                    <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                        {contacts.map((c) => (
                            <div key={c.id} style={{ padding: 12, background: "#FAFAF7", borderRadius: 3 }} data-testid={`portal-contatto-${c.id}`}>
                                <div style={{ fontWeight: 600, color: "#181B20" }}>{c.name}{c.role_label && <span style={{ color: "#5F6560", fontWeight: 400 }}> · {c.role_label}</span>}</div>
                                <div style={{ fontSize: 13, color: "#5F6560", marginTop: 4 }}>
                                    {c.email && <a href={`mailto:${c.email}`} className="lt-link">{c.email}</a>}
                                    {c.email && c.phone && <span> · </span>}
                                    {c.phone && <a href={`tel:${c.phone}`} className="lt-link">{c.phone}</a>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {allNotes.length > 0 && (
                <div className="lt-panel" style={{ padding: 24, marginTop: 20 }} data-testid="portal-note-enerleft">
                    <h2 className="lt-section-title">Note da ENERLEFT</h2>
                    <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                        {allNotes.map((n) => {
                            const isMeeting = n.source === "meeting";
                            return (
                                <div
                                    key={n.id}
                                    style={{
                                        padding: 12, background: "#FAFAF7", borderRadius: 3,
                                        borderLeft: isMeeting ? "3px solid #3B82F6" : "3px solid #EFEFEC",
                                    }}
                                    data-testid={`portal-nota-${n.id}`}
                                >
                                    <div style={{ fontSize: 12, color: "#8A8F88", fontFamily: '"IBM Plex Mono", monospace', letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>
                                        {n.author} · {new Date(n.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                                    </div>
                                    {isMeeting && (
                                        <div
                                            data-testid={`portal-meeting-note-badge-${n.id}`}
                                            style={{
                                                display: "inline-block", marginBottom: 8,
                                                padding: "3px 8px", background: "#DBEAFE", color: "#1E40AF",
                                                borderRadius: 3, fontFamily: '"IBM Plex Mono", monospace',
                                                fontSize: 10.5, letterSpacing: "0.05em",
                                                textTransform: "uppercase", fontWeight: 600,
                                            }}
                                        >
                                            Dalla riunione del {new Date(n.meeting_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                                        </div>
                                    )}
                                    <div style={{ fontSize: 14, color: "#181B20", whiteSpace: "pre-wrap" }}>{n.body}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// === Portal Tickets ===
export function PortalTicketList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("all");
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        const params = {}; if (status !== "all") params.status = status;
        try { const { data } = await api.get("/client/tickets", { params }); setItems(data.items || []); }
        finally { setLoading(false); }
    }, [status]);
    useEffect(() => { load(); }, [load]);
    return (
        <div style={{ padding: "32px 40px", maxWidth: 1100 }} data-testid="portal-tickets-list">
            <div className="flex items-center justify-between" style={{ marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
                <h1 className="lt-h1">Assistenza</h1>
                <button className="lt-btn-primary" onClick={() => navigate("/portal/ticket/nuovo")} data-testid="portal-ticket-new-btn"><Plus size={16} /> Apri ticket</button>
            </div>
            <div className="lt-seg" style={{ marginBottom: 16 }} data-testid="portal-tickets-status-filter">
                {[["all", "Tutti"], ["aperto", "Aperti"], ["in_lavorazione", "In lavorazione"], ["in_attesa", "In attesa"], ["chiuso", "Chiusi"]].map(([k, l]) => (
                    <button key={k} type="button" className={status === k ? "is-active" : ""} onClick={() => setStatus(k)}>{l}</button>
                ))}
            </div>
            {loading ? <div style={{ color: "#8A8F88" }}>Caricamento…</div>
                : items.length === 0 ? (
                    <div className="lt-card" style={{ padding: 32, textAlign: "center" }} data-testid="portal-tickets-empty">
                        <p className="lt-sub">Non hai ancora aperto ticket.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {items.map((t) => (
                            <div key={t.id} className="lt-panel" style={{ padding: 18, cursor: "pointer" }} onClick={() => navigate(`/portal/ticket/${t.id}`)} data-testid={`portal-ticket-row-${t.id}`}>
                                <div className="flex items-center justify-between gap-3" style={{ flexWrap: "wrap" }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: "#181B20", fontSize: 15 }}>{t.title}</div>
                                        <div style={{ color: "#8A8F88", fontSize: 12, marginTop: 4 }}>{t.category?.name} · Aperto il {fmtDateTime(t.created_at)}</div>
                                    </div>
                                    <StatusChip status={t.status} />
                                </div>
                                <div style={{ color: "#5F6560", fontSize: 12, marginTop: 6 }}>Ultimo messaggio: {fmtDateTime(t.last_message_at)}</div>
                            </div>
                        ))}
                    </div>
                )}
        </div>
    );
}

export function PortalTicketNew() {
    const navigate = useNavigate();
    const [cats, setCats] = useState([]);
    const [title, setTitle] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    useEffect(() => { api.get("/client/ticket-categories").then(({ data }) => setCats(data.items || [])); }, []);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const re = params.get("re"); if (re) setTitle(`RE: ${re}`);
    }, [location.search]);
    const submit = async (e) => {
        e.preventDefault(); setError(""); setLoading(true);
        try {
            const { data } = await api.post("/client/tickets", { title: title.trim(), category_id: categoryId, message: message.trim() });
            toast.success("Ticket inviato"); navigate(`/portal/ticket/${data.id}`, { replace: true });
        } catch (err) { setError(err?.response?.data?.detail || "Errore"); }
        finally { setLoading(false); }
    };
    return (
        <div style={{ padding: "32px 40px", maxWidth: 720 }} data-testid="portal-ticket-new">
            <Link to="/portal/ticket" className="lt-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 20 }}><ArrowLeft size={14} /> Torna all'assistenza</Link>
            <h1 className="lt-h1" style={{ marginBottom: 8 }}>Apri un ticket</h1>
            <p className="lt-sub" style={{ marginBottom: 24 }}>Descrivi la tua richiesta. Ti risponderemo appena possibile.</p>
            <form onSubmit={submit} className="lt-card" style={{ padding: 32 }}>
                <label className="lt-label">Titolo</label>
                <input className="lt-field" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} data-testid="portal-tnew-title" />
                <label className="lt-label">Categoria</label>
                <select className="lt-field" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required data-testid="portal-tnew-category">
                    <option value="" disabled>Seleziona…</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <label className="lt-label">Messaggio</label>
                <textarea className="lt-field" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} required data-testid="portal-tnew-message" />
                {error && <div style={{ fontSize: 13, color: "#B23A48", marginBottom: 12 }}>{error}</div>}
                <div className="flex items-center justify-end gap-3">
                    <Link to="/portal/ticket" className="lt-btn-secondary">Annulla</Link>
                    <button className="lt-btn-primary" disabled={loading} data-testid="portal-tnew-submit">{loading ? "Invio…" : "Invia ticket"}</button>
                </div>
            </form>
        </div>
    );
}

export function PortalTicketDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [t, setT] = useState(null);
    const [text, setText] = useState("");
    const load = useCallback(async () => {
        try { const { data } = await api.get(`/client/tickets/${id}`); setT(data); }
        catch { toast.error("Ticket non trovato"); navigate("/portal/ticket"); }
    }, [id, navigate]);
    useEffect(() => { load(); }, [load]);
    if (!t) return <div style={{ padding: 40, color: "#8A8F88" }}>Caricamento…</div>;
    const reply = async (e) => {
        e.preventDefault();
        try { await api.post(`/client/tickets/${id}/messages`, { text: text.trim() }); setText(""); load(); toast.success("Messaggio inviato"); }
        catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    };
    return (
        <div style={{ padding: "32px 40px", maxWidth: 880 }} data-testid="portal-ticket-detail">
            <Link to="/portal/ticket" className="lt-link" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 20 }}><ArrowLeft size={14} /> Torna all'assistenza</Link>
            <div className="lt-card" style={{ padding: 28 }}>
                <div className="flex items-start justify-between gap-3" style={{ flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h1 className="lt-h2">{t.title}</h1>
                        <p style={{ fontSize: 13, color: "#8A8F88", margin: "6px 0 0 0" }}>{t.category?.name} · Aperto il {fmtDateTime(t.created_at)}</p>
                    </div>
                    <StatusChip status={t.status} />
                </div>
                <div className="flex flex-col gap-3" style={{ marginTop: 16 }}>
                    {t.messages?.map((m) => (
                        <div key={m.id} className={m.author_user_type === "internal" ? "lt-msg-internal" : "lt-msg-client"}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="lt-msg-author">{m.author_name}</span>
                                    <span style={{ fontSize: 12, color: "#8A8F88", marginLeft: 8 }}>{m.author_user_type === "internal" ? "ENERLEFT" : ""}</span>
                                </div>
                                <span style={{ fontSize: 12, color: "#8A8F88" }}>{fmtDateTime(m.created_at)}</span>
                            </div>
                            <div className="lt-msg-body">{m.text}</div>
                        </div>
                    ))}
                </div>
                {t.status === "chiuso" ? (
                    <div style={{ marginTop: 20, padding: 16, background: "#FAFAF7", border: "1px solid #E3E5E1", borderRadius: 12 }} data-testid="portal-ticket-closed-banner">
                        <p style={{ margin: 0, color: "#5F6560", fontSize: 14 }}>Questo ticket è stato chiuso da ENERLEFT. Per riaprirlo, contatta il tuo referente o apri un nuovo ticket.</p>
                        <button className="lt-btn-secondary" style={{ marginTop: 12 }} onClick={() => navigate(`/portal/ticket/nuovo?re=${encodeURIComponent(t.title)}`)}>Apri nuovo ticket</button>
                    </div>
                ) : (
                    <form onSubmit={reply} style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #E3E5E1" }} data-testid="portal-reply-form">
                        <label className="lt-label">Risposta</label>
                        <textarea className="lt-field" rows={4} value={text} onChange={(e) => setText(e.target.value)} required data-testid="portal-reply-text" />
                        <button className="lt-btn-primary" type="submit" data-testid="portal-reply-submit">Pubblica risposta</button>
                    </form>
                )}
            </div>
        </div>
    );
}
